import { useLocation } from "react-router-dom";

/**
 * Site-wide animated background: slowly drifting purple/orange/pink glows over a
 * faint panning grid. Rendered once in App, fixed behind every page (z-index -1),
 * so page roots must not paint an opaque background of their own.
 *
 * Public pages get the full effect; logged-in app pages get a quieter version so
 * tables and forms stay easy to read. All motion stops under prefers-reduced-motion.
 */

const PUBLIC_PREFIXES = [
  "/home",
  "/pricing",
  "/features",
  "/insurance",
  "/trial",
  "/register",
  "/onboarding",
  "/login",
  "/setup",
  "/phone-system",
];

function isPublicPath(pathname: string): boolean {
  if (pathname === "/") return true;
  return PUBLIC_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

const STYLE = `
  .ns-bg {
    position: fixed;
    inset: 0;
    z-index: -1;
    overflow: hidden;
    pointer-events: none;
    transition: opacity 0.6s ease;
  }
  .ns-bg--subtle { opacity: 0.5; }
  .ns-bg-orb {
    position: absolute;
    border-radius: 9999px;
    filter: blur(90px);
    will-change: transform;
  }
  .ns-bg-orb-1 { width: 520px; height: 520px; top: -120px; left: -140px; background: rgba(138, 43, 226, 0.28); animation: ns-bg-drift-1 26s ease-in-out infinite alternate; }
  .ns-bg-orb-2 { width: 460px; height: 460px; top: 35%; right: -160px; background: rgba(255, 106, 0, 0.18); animation: ns-bg-drift-2 32s ease-in-out infinite alternate; }
  .ns-bg-orb-3 { width: 420px; height: 420px; bottom: -160px; left: 25%; background: rgba(236, 72, 153, 0.14); animation: ns-bg-drift-3 38s ease-in-out infinite alternate; }
  @keyframes ns-bg-drift-1 { from { transform: translate(0, 0) scale(1); } to { transform: translate(18vw, 22vh) scale(1.15); } }
  @keyframes ns-bg-drift-2 { from { transform: translate(0, 0) scale(1.1); } to { transform: translate(-22vw, -14vh) scale(0.9); } }
  @keyframes ns-bg-drift-3 { from { transform: translate(0, 0) scale(0.95); } to { transform: translate(-12vw, -26vh) scale(1.2); } }
  .ns-bg-grid {
    position: absolute;
    inset: -80px;
    background-image:
      linear-gradient(rgba(138, 43, 226, 0.07) 1px, transparent 1px),
      linear-gradient(90deg, rgba(138, 43, 226, 0.07) 1px, transparent 1px);
    background-size: 80px 80px;
    mask-image: radial-gradient(ellipse at 50% 30%, black 20%, transparent 75%);
    -webkit-mask-image: radial-gradient(ellipse at 50% 30%, black 20%, transparent 75%);
    animation: ns-bg-grid-pan 30s linear infinite;
  }
  @keyframes ns-bg-grid-pan { from { transform: translate(0, 0); } to { transform: translate(80px, 80px); } }
  @media (prefers-reduced-motion: reduce) {
    .ns-bg-orb, .ns-bg-grid { animation: none !important; }
  }
`;

export default function MovingBackground() {
  const { pathname } = useLocation();
  const subtle = !isPublicPath(pathname);

  return (
    <>
      <style>{STYLE}</style>
      <div className={`ns-bg${subtle ? " ns-bg--subtle" : ""}`} aria-hidden="true">
        <div className="ns-bg-grid" />
        <div className="ns-bg-orb ns-bg-orb-1" />
        <div className="ns-bg-orb ns-bg-orb-2" />
        <div className="ns-bg-orb ns-bg-orb-3" />
      </div>
    </>
  );
}
